import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import { BookOpen, Heart, Calendar, Brain, Home, ChevronRight, BookMarked, Bell, Trophy, BarChart3, Settings, Star, Zap, Clock, MessageSquare, FileText, Monitor, QrCode, Users, LayoutDashboard, Bookmark, RefreshCw, Eye, Activity } from 'lucide-react';



export default function TutorialPage() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Site Overview', icon: Home },
    { id: 'dashboard', title: 'Dashboard', icon: LayoutDashboard },
    { id: 'bible-reader', title: 'Bible Reader', icon: BookOpen },
    { id: 'my-verses', title: 'Verse Library', icon: Heart },
    { id: 'reading-plans', title: 'Reading Plans', icon: Calendar },
    { id: 'memorization', title: 'Memorization', icon: Brain },
    { id: 'daily-review', title: 'Daily Review', icon: Clock },
    { id: 'study-notes', title: 'Study & Sermon Notes', icon: FileText },
    { id: 'analytics', title: 'Study Analytics', icon: BarChart3 },
    { id: 'awards', title: 'Talents & Awards', icon: Trophy },
    { id: 'notifications', title: 'Notifications', icon: Bell },
    { id: 'profile', title: 'Profile & Settings', icon: Settings },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const SectionHeader = ({ icon: SectionIcon = null, title }) => (
    <div className="flex items-center space-x-3 mb-6">
      <SectionIcon className="w-8 h-8 text-primary" />
      <h2 className="text-3xl font-bold text-foreground">{title}</h2>
    </div>
  );

  const InfoBox = ({ variant = 'primary', icon: InfoIcon, title, children }) => {
    const styles = {
      primary: 'bg-primary/10 border-l-4 border-primary',
      success: 'bg-success/10 border-l-4 border-success',
      warning: 'bg-warning/10 border-l-4 border-warning',
      accent: 'bg-accent/10 border-l-4 border-accent',
    };
    const textStyles = {
      primary: 'text-primary',
      success: 'text-success',
      warning: 'text-warning',
      accent: 'text-accent',
    };
    return (
      <div className={`${styles?.[variant]} p-4 rounded`}>
        <h3 className={`text-xl font-semibold ${textStyles?.[variant]} mb-3 flex items-center space-x-2`}>
          {InfoIcon && <InfoIcon className="w-5 h-5" />}
          <span>{title}</span>
        </h3>
        <div className={`${textStyles?.[variant]} opacity-90 leading-relaxed`}>{children}</div>
      </div>
    );
  };

  const PhaseBox = ({ phase, interval, description }) => (
    <div className="bg-muted/50 rounded-lg p-3 border border-border">
      <p className="font-semibold text-foreground">{phase} <span className="text-primary text-sm">({interval})</span></p>
      <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
    </div>
  );

  const ReviewButton = ({ label, sublabel, variant }) => {
    const styles = {
      destructive: 'bg-destructive/10 border-destructive/30 text-destructive',
      warning: 'bg-warning/10 border-warning/30 text-warning',
      primary: 'bg-primary/10 border-primary/30 text-primary',
      success: 'bg-success/10 border-success/30 text-success',
    };
    return (
      <div className={`${styles?.[variant]} border rounded-lg p-3`}>
        <p className="font-semibold">{label}</p>
        <p className="text-sm opacity-80 mt-0.5">{sublabel}</p>
      </div>
    );
  };

  // Screenshot placeholder component — replace src with actual image paths
  // To add a screenshot: upload the image to public/assets/images/ and replace
  // the placeholder text with: <ScreenshotPlaceholder src="/assets/images/your-screenshot.png" alt="Description" />
  const ScreenshotPlaceholder = ({ src = '', alt, caption = '' }) => (
    <figure className="my-4">
      {src ? (
        <img src={src} alt={alt} className="w-full rounded-lg border border-border shadow-sm" />
      ) : (
        <div className="w-full h-40 bg-muted/50 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center text-muted-foreground">
          <Eye className="w-8 h-8 mb-2 opacity-40" />
          <span className="text-sm font-medium opacity-60">[Screenshot: {alt}]</span>
          <span className="text-xs opacity-40 mt-1">Replace with: /assets/images/your-file.png</span>
        </div>
      )}
      {caption && <figcaption className="text-xs text-muted-foreground text-center mt-2 italic">{caption}</figcaption>}
    </figure>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-20 md:pb-6">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-primary rounded-lg">
              <BookMarked className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-4xl font-bold text-foreground">Tutorial</h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Learn how to use Fortified Mind to strengthen your scripture memory and deepen your Bible study.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-20 bg-card border border-border rounded-lg p-4 shadow-warm">
              <h2 className="text-lg font-semibold text-foreground mb-4">Sections</h2>
              <nav className="space-y-1">
                {sections?.map((section) => {
                  const SectionIcon = section?.icon;
                  return (
                    <button
                      key={section?.id}
                      onClick={() => scrollToSection(section?.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg text-left transition-all duration-200 ${
                        activeSection === section?.id
                          ? 'bg-primary text-primary-foreground shadow-warm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <SectionIcon size={16} />
                      <span className="text-sm font-medium">{section?.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-12">

            {/* ── OVERVIEW ── */}
            <section id="overview" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Home} title="General Site Overview" />
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Purpose</h3>
                  <p className="text-muted-foreground leading-relaxed italic border-l-4 border-primary pl-4 py-1">
                    "I will meditate in thy precepts, and have respect unto thy ways. I will delight myself in thy statutes: I will not forget thy word." (Psalms 119:15-16)
                  </p>
                  <p className="text-muted-foreground leading-relaxed mt-3">
                    Fortified Mind is designed to help you memorize and meditate on God's Word through scientifically-proven spaced repetition techniques. Our mission is to make scripture memory accessible, effective, and spiritually enrichinging.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Feature Overview</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    {[
                      ['Dashboard', 'Your home base — daily review card, Memory Health indicator, reading plan progress, and quick access tiles'],
                      ['Bible Reader', 'Browse scripture with commentaries, Strong\'s numbers, highlights, bookmarks, cross-references, and social engagement'],
                      ['Verse Library', 'Manage your personal verse library with spaced repetition scheduling, extra review, and verse details'],
                      ['Reading Plans', 'Follow structured Bible and EGW reading plans with reflection features'],
                      ['Memorization', 'Use progressive chunking, word-order, and progressive-reveal modes with AI-powered TTS'],
                      ['Daily Review', 'Spaced repetition review system to keep verses fresh in your memory'],
                      ['Study & Sermon Notes', 'Build slide-based sermon or study notes and present them live with real-time audience sync'],
                      ['Study Analytics', 'Track your progress, streaks, phase distribution, and verse mastery over time'],
                      ['Talents & Awards', 'Earn badges and talents as you reach scripture memory milestones'],
                      ['Notifications', 'Customizable daily reminders to keep you on track'],
                    ]?.map(([title, desc]) => (
                      <li key={title} className="flex items-start space-x-2">
                        <ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span><strong className="text-foreground">{title}:</strong> {desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Navigation</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Use the top navigation bar to access all major features. The dashboard provides quick access to daily reviews, reading plans, and progress summaries. Your profile menu (top right) contains settings, privacy controls, notification preferences, and account management.
                  </p>
                </div>
              </div>
            </section>

            {/* ── DASHBOARD ── */}
            <section id="dashboard" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={LayoutDashboard} title="Dashboard" />
              <div className="space-y-6">
                <p className="text-muted-foreground leading-relaxed">
                  The Dashboard is your home base. It gives you an at-a-glance view of your scripture memory progress and provides quick access to the most important features.
                </p>
                <ScreenshotPlaceholder alt="Dashboard overview" caption="The main dashboard showing Memory Health, Daily Review card, and Quick Access tiles" src="/assets/images/dashboard-overview.png" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Dashboard Cards</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Daily Review Card:</strong> Shows how many verses are due for review today. Click <strong className="text-foreground">"Start Review"</strong> to begin your session immediately.</li>
                    <li><strong className="text-foreground">Memory Health Card:</strong> A color-coded indicator of how well you are keeping up with your reviews. See the Memory Health section below for details.</li>
                    <li><strong className="text-foreground">Progress Summary:</strong> Total verses memorized, current streak, and phase distribution at a glance.</li>
                    <li><strong className="text-foreground">Reading Plans Card:</strong> Shows your active reading plan and today's reading assignment. Click to open the reading interface directly.</li>
                    <li><strong className="text-foreground">Daily Verse Card:</strong> A featured verse of the day for inspiration and meditation.</li>
                    <li><strong className="text-foreground">Quick Access Tiles:</strong> One-tap shortcuts to Bible Reader, Verse Library, Memorization Practice, Study Notes, and Analytics.</li>
                    <li><strong className="text-foreground">Recent Activity:</strong> A log of your latest reviews, verses added, and milestones achieved.</li>
                    <li><strong className="text-foreground">Motivational Section:</strong> Encouraging scripture quotes and progress affirmations to keep you motivated.</li>
                  </ul>
                </div>

                <InfoBox variant="primary" icon={Activity} title="Memory Health Indicator">
                  <p className="mb-2">
                    The <strong>Memory Health</strong> card shows the overall health of your verse library based on how up-to-date your reviews are. It uses a color-coded system:
                  </p>
                  <ul className="space-y-1 text-sm">
                    <li>🟢 <strong>Excellent</strong> — All or nearly all due reviews are completed. Your memory is in great shape.</li>
                    <li>🟡 <strong>Good</strong> — A small backlog of overdue reviews. Try to catch up within a day or two.</li>
                    <li>🟠 <strong>Fair</strong> — A moderate backlog. Several verses are at risk of being forgotten.</li>
                    <li>🔴 <strong>Needs Attention</strong> — Many overdue reviews. Verses may be fading from memory. Prioritize your daily review session.</li>
                  </ul>
                  <p className="mt-2">
                    The health score is calculated from the ratio of overdue verses to your total active verse count. Completing your daily review each day keeps this score green.
                  </p>
                </InfoBox>
              </div>
            </section>

            {/* ── BIBLE READER ── */}
            <section id="bible-reader" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={BookOpen} title="Bible Reader" />
              <div className="space-y-6">
                <ScreenshotPlaceholder alt="Bible Reader main view" caption="The Bible Reader showing verse text, action toolbar, and commentary panel" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">How to Add a Verse to Your Library</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>Navigate to the Bible Reader from the top menu</li>
                    <li>Find the verse you want to memorize</li>
                    <li>Click the <strong className="text-foreground">"Add to Library"</strong> button below the verse</li>
                    <li>Select the initial phase (Phase 0 for Future Verses, Phase 1+ if already memorized)</li>
                    <li>The verse is now saved to your library for spaced repetition review</li>
                  </ol>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Verse Highlights</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Highlight any verse by clicking the <strong className="text-foreground">Highlight</strong> icon in the verse action toolbar. Choose from multiple highlight colors to color-code verses by theme or importance. Highlights are saved to your account and visible every time you return to that passage. Remove a highlight by clicking the highlight icon again on an already-highlighted verse.
                  </p>
                  <ScreenshotPlaceholder alt="Verse highlight color picker" caption="Selecting a highlight color from the verse action toolbar" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Bookmarks</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Bookmark any verse by clicking the <strong className="text-foreground">Bookmark</strong> icon in the verse toolbar. Access all your bookmarks from the <strong className="text-foreground">Bookmarks panel</strong> (bookmark icon in the reader toolbar). Click any bookmark to jump directly to that verse. Bookmarks are organized by book and chapter for easy navigation.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Cross References</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Click the <strong className="text-foreground">Cross References</strong> icon on any verse to open a side panel showing related passages from across the Bible. Cross references are sourced from the Treasury of Scripture Knowledge (TSK) and help you see how scripture interprets scripture. Click any cross reference to navigate to that passage instantly.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Linked Verses</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Create your own custom verse links by clicking <strong className="text-foreground">"Add Link"</strong> in the verse action menu. Linked verses are personal connections you draw between passages — useful for thematic study, sermon preparation, or tracking prophecy and fulfillment. Your linked verses are private and visible only to you.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Available Commentaries</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">SDA Bible Commentary:</strong> Seventh-day Adventist theological perspective</li>
                    <li><strong className="text-foreground">Ellen G. White Comments:</strong> Inspirational insights from Ellen White's writings</li>
                    <li><strong className="text-foreground">Treasury of Scripture Knowledge (TSK):</strong> Cross-references and parallel passages</li>
                    <li><strong className="text-foreground">Matthew Henry Commentary:</strong> Classic devotional commentary</li>
                    <li><strong className="text-foreground">Cambridge Bible Commentary:</strong> Academic scholarly notes</li>
                    <li><strong className="text-foreground">Benson Commentary:</strong> Verse-by-verse exposition</li>
                    <li><strong className="text-foreground">Pulpit Commentary:</strong> Homiletical and expository notes</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Strong's Numbers & Interlinear</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Toggle <strong className="text-foreground">"Show Strong's Numbers"</strong> to see original Hebrew/Greek word references. Click any Strong's number to view the original word meaning, pronunciation, and usage examples. The Interlinear view shows the original language word-for-word beneath the English text.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Bible Versions</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Switch between multiple Bible translations using the version selector. The system supports KJV, ASV, and additional versions imported by administrators. Compare translations side by side for deeper study.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Social Engagement Through Reflection</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Share your thoughts by clicking <strong className="text-foreground">"Add Reflection"</strong> below any verse. Your reflections can be public (visible to all users) or private (personal notes). Engage with others' reflections through likes and thoughtful responses. Control visibility in your Profile → Privacy settings.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Audio Playback</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Use the floating audio player to listen to Bible chapters read aloud. The audio player supports playback speed control and can be minimized while you continue reading. Neural TTS voices provide natural-sounding scripture narration.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Reading Progress Tracker</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    The Bible Reader automatically tracks which chapters you have read. Your reading progress is reflected in the Study Analytics dashboard and contributes to your overall engagement statistics.
                  </p>
                </div>
              </div>
            </section>

            {/* ── VERSE LIBRARY ── */}
            <section id="my-verses" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Heart} title="Verse Library" />
              <div className="space-y-6">
                <ScreenshotPlaceholder alt="Verse Library overview" caption="The Verse Library showing verse cards, phase filters, and library stats" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Adding Verses to Your Library</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Add verses from the Bible Reader or click <strong className="text-foreground">"Add Verse"</strong> in the Verse Library to manually enter a reference. You can also import verses in bulk using the import feature, or add entire curated verse lists by topic.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Verse Detail Modal</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Click the <strong className="text-foreground">expand icon</strong> on any verse card to open the Verse Detail Modal. This gives you a full view of the verse including:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Full verse text with your selected Bible version</li>
                    <li>Current memorization phase and next review date</li>
                    <li>Review history — how many times reviewed and your success rate</li>
                    <li>Phase progression timeline</li>
                    <li>Quick actions: Edit phase, trigger Extra Review, open in Bible Reader</li>
                    <li>Personal reflection notes attached to the verse</li>
                  </ul>
                  <ScreenshotPlaceholder alt="Verse Detail Modal" caption="The Verse Detail Modal showing review history and phase progression" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Extra Review</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    If you want to practice a specific verse outside of your normal daily review schedule, use <strong className="text-foreground">Extra Review</strong>. Click the <strong className="text-foreground">Extra Review</strong> button (circular arrow icon) on any verse card or from the Verse Detail Modal. This opens a focused review session for just that verse without affecting its spaced repetition schedule.
                  </p>
                  <InfoBox variant="accent" icon={RefreshCw} title="When to Use Extra Review">
                    <p>
                      Use Extra Review when you want to drill a verse you're struggling with, practice before a presentation, or simply revisit a favorite passage. It does <strong>not</strong> reset or advance the verse's phase — it's purely for additional practice.
                    </p>
                  </InfoBox>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Phase 0 — Future Verses</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Phase 0 is a holding area for verses you plan to memorize in the future. They do not appear in your daily review queue until you manually promote them to Phase 1. Use the <strong className="text-foreground">Phase 0 Verses Card</strong> on the dashboard or filter by Phase 0 in the Verse Library to manage your future verse queue. Promote a verse by clicking <strong className="text-foreground">"Start Memorizing"</strong> on the verse card.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Spaced Repetition Phases Explained</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <PhaseBox phase="Phase 0" interval="Future" description="Verses queued for future memorization. Not yet in active review." />
                    <PhaseBox phase="Phase 1" interval="Daily" description="Actively memorizing. Review every day until mastered." />
                    <PhaseBox phase="Phase 2" interval="3 days" description="Strengthening memory. Review after 3 days." />
                    <PhaseBox phase="Phase 3" interval="7 days" description="Building long-term retention. Review weekly." />
                    <PhaseBox phase="Phase 4" interval="14 days" description="Strong memory. Review bi-weekly." />
                    <PhaseBox phase="Phase 5" interval="30 days" description="Well-memorized. Review monthly." />
                    <PhaseBox phase="Phase 6+" interval="60+ days" description="Mastered verses. Review every 2+ months for maintenance." />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Filtering & Searching</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Use the <strong className="text-foreground">Filter Panel</strong> to narrow your library by phase, book, tag, or review status. The search bar lets you find verses by reference or keyword. Combine filters for precise results — for example, show only Phase 1 verses from the Psalms.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Curated Verse Lists</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Browse topical verse lists curated by the Fortified Mind team (e.g., Faith, Prayer, Salvation). Add an entire list to your library with one click to begin a focused memorization journey on a specific theme.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Bulk Actions</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Select multiple verses using the checkboxes on each card, then use the <strong className="text-foreground">Bulk Actions</strong> toolbar to change phase, delete, or export the selected verses all at once.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Bulk Import & Export</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Import verses in bulk via CSV or JSON. Export your entire verse library for backup or use in other apps. Access these features from the Verse Library toolbar.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Library Stats</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    The <strong className="text-foreground">Library Stats</strong> panel at the top of the Verse Library shows your total verse count, phase distribution breakdown, and a quick analytics preview. Click <strong className="text-foreground">"View Full Analytics"</strong> to go to the Study Analytics dashboard.
                  </p>
                </div>
              </div>
            </section>

            {/* ── READING PLANS ── */}
            <section id="reading-plans" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Calendar} title="Reading Plans" />
              <div className="space-y-6">
                <ScreenshotPlaceholder alt="Reading Plans page" caption="The Reading Plans page showing available plans and active plan progress" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Enrolling in a Reading Plan</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>Navigate to <strong className="text-foreground">Reading Plans</strong> from the top menu</li>
                    <li>Browse available plans by category (Chronological, Thematic, Devotional, etc.)</li>
                    <li>Click <strong className="text-foreground">"Start Plan"</strong> on your chosen plan</li>
                    <li>Configure your start date and reading preferences</li>
                    <li>Begin reading according to the daily schedule</li>
                  </ol>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Reading Calendar</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Each reading plan includes a <strong className="text-foreground">Reading Calendar</strong> that shows your progress day by day. Completed days are marked in green. Click any past day to review what you read. The calendar also shows upcoming reading assignments so you can plan ahead.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">EGW Reading Plans</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    In addition to Bible reading plans, Fortified Mind offers structured Ellen G. White reading plans. Switch to the <strong className="text-foreground">"EGW Plans"</strong> tab on the Reading Plans page to browse and enroll in plans covering works like Steps to Christ, Desire of Ages, and Great Controversy.
                  </p>
                </div>
                <InfoBox variant="warning" icon={ChevronRight} title="Important: It's Not About Speed">
                  <p>
                    Reading plans are designed for <strong>meditation and reflection</strong>, not rapid completion. The goal is to slow down, absorb God's Word deeply, and allow it to transform your life. Don't rush through passages to earn badges—instead, take time to reflect, pray, and apply what you read.
                  </p>
                </InfoBox>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Reading Plan Notifications</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Set up daily reading reminders in <strong className="text-foreground">Profile → Notifications</strong>. You can opt in or out of plan emails, set your preferred reminder time, and choose how frequently you receive reminders.
                  </p>
                </div>
              </div>
            </section>

            {/* ── MEMORIZATION ── */}
            <section id="memorization" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Brain} title="Memorization Practice" />
              <div className="space-y-6">
                <InfoBox variant="primary" icon={Zap} title="The Chunking Method">
                  <p>
                    Our progressive chunking method breaks verses into small, manageable pieces that build upon each other. This scientifically-proven technique leverages how your brain naturally learns and retains information. By repeating progressively longer chunks, you create strong neural pathways for effortless recall.
                  </p>
                </InfoBox>
                <ScreenshotPlaceholder alt="Memorization Practice mode selector" caption="The Memorization Practice page showing mode selection options" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Practice Modes</h3>
                  <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
                    <li>
                      <strong className="text-foreground">Progressive Chunking:</strong> The primary memorization mode. The verse is broken into chunks that grow progressively longer. Audio plays each chunk automatically, and you repeat it aloud. This builds strong neural pathways through repetition and audio reinforcement.
                    </li>
                    <li>
                      <strong className="text-foreground">Word Order Mode:</strong> Scrambles the words of the verse and challenges you to arrange them in the correct order by clicking or dragging. This mode tests your knowledge of the verse structure and is excellent for reinforcing exact wording.
                    </li>
                    <li>
                      <strong className="text-foreground">Progressive Reveal Mode:</strong> Displays the verse with words progressively hidden (replaced by blanks) as you advance through levels. Start with the full verse visible, then practice with more and more words hidden until you can recite the entire verse from memory.
                    </li>
                    <li>
                      <strong className="text-foreground">Sequential Mode:</strong> Practice verses from your review queue one after another without setup screens — ideal for a quick, focused session when you want to run through multiple verses efficiently.
                    </li>
                    <li>
                      <strong className="text-foreground">Verse Range:</strong> Select a specific range of verses (e.g., Psalms 23:1–6) for a focused practice session on a passage rather than individual verses.
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">How to Memorize a Verse (Progressive Chunking)</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>Select a verse from your library or Bible Reader</li>
                    <li>Click <strong className="text-foreground">"Memorize"</strong> to open the memorization interface</li>
                    <li>Choose <strong className="text-foreground">"Progressive Chunking"</strong> mode</li>
                    <li>Configure chunk size (3–10 words) and repetitions (5–20 times)</li>
                    <li>Enable <strong className="text-foreground">Autoplay</strong> to hear the verse read aloud progressively</li>
                    <li>Repeat each chunk out loud as it's spoken</li>
                    <li>Progress through increasingly longer chunks until you've mastered the full verse</li>
                  </ol>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Neural TTS Voices</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Fortified Mind uses Google's Neural2 text-to-speech technology for natural-sounding scripture narration. Select from male or female voices and adjust playback speed (slow, normal, fast) to match your learning pace. The system caches audio for offline use.
                  </p>
                </div>
                <InfoBox variant="success" icon={BookMarked} title="Best Practice: Reference → Verse → Reference">
                  <p className="mb-3">
                    Always memorize verses using the <strong>REFERENCE, VERSE, REFERENCE</strong> pattern. For example:
                  </p>
                  <div className="bg-card rounded p-3 font-mono text-sm text-success border border-success/20">
                    "John 3:16 — For God so loved the world... — John 3:16"
                  </div>
                  <p className="mt-3">
                    This ensures you can recall the verse when you hear the reference AND identify the reference when you hear the verse.
                  </p>
                </InfoBox>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Chunking Controls</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Autoplay:</strong> Automatically progresses through all chunks</li>
                    <li><strong className="text-foreground">Speed Control:</strong> Adjust reading speed (slow, normal, fast)</li>
                    <li><strong className="text-foreground">Voice Selection:</strong> Choose male or female Neural2 voice</li>
                    <li><strong className="text-foreground">Chunk Size:</strong> Set word count per chunk (3–10 words)</li>
                    <li><strong className="text-foreground">Repetitions:</strong> Configure how many times each chunk repeats (5–20 times)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Session Stats & Results</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    After completing a memorization session, the <strong className="text-foreground">Session Stats</strong> screen shows how many verses you practiced, time spent, and your accuracy. A <strong className="text-foreground">Verse Completion Modal</strong> appears when you finish a verse, celebrating your progress and offering to advance the verse to the next phase.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Keyboard Shortcuts</h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <ul className="space-y-2 text-muted-foreground">
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">Space</kbd> — Play / Pause</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">←</kbd> — Previous chunk level</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">→</kbd> — Next chunk level</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* ── DAILY REVIEW ── */}
            <section id="daily-review" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Clock} title="Daily Review System" />
              <div className="space-y-6">
                <ScreenshotPlaceholder alt="Daily Review session" caption="A Daily Review session showing a verse card with recall rating buttons" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Starting a Daily Review</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>Click <strong className="text-foreground">"Start Daily Review"</strong> from the Dashboard or Verse Library</li>
                    <li>The system presents verses due for review today based on spaced repetition</li>
                    <li>Attempt to recall each verse before revealing the text</li>
                    <li>Rate your recall accuracy using the provided buttons</li>
                    <li>The system adjusts future review dates based on your performance</li>
                  </ol>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Review Button Actions</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <ReviewButton label="Again (Forgot)" sublabel="Moves verse back to Phase 0. Use when you couldn't recall the verse." variant="destructive" />
                    <ReviewButton label="Hard (Struggled)" sublabel="Keeps verse in current phase. Use when recall was difficult but successful." variant="warning" />
                    <ReviewButton label="Good (Recalled)" sublabel="Advances to next phase. Use when you recalled correctly with some effort." variant="primary" />
                    <ReviewButton label="Easy (Perfect)" sublabel="Advances to next phase. Use when recall was effortless and immediate." variant="success" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Review Calendar</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    The review calendar shows your daily review history and upcoming review counts. Green days indicate completed reviews; amber days show partial completion. Use it to plan your study schedule and maintain your streak.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Daily Review Limit</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    A daily review limit can be configured in <strong className="text-foreground">Profile → Advanced</strong> to prevent review fatigue. When the limit is reached, the system will let you know and encourage you to return tomorrow. This helps maintain consistency without burnout.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Keyboard Shortcuts</h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <ul className="space-y-2 text-muted-foreground">
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">Space</kbd> — Reveal verse / Next verse</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">1</kbd> — Again (Forgot)</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">2</kbd> — Hard (Struggled)</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">3</kbd> — Good (Recalled)</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">4</kbd> — Easy (Perfect)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* ── STUDY & SERMON NOTES ── */}
            <section id="study-notes" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={FileText} title="Study & Sermon Notes" />
              <div className="space-y-6">
                <div>
                  <p className="text-muted-foreground leading-relaxed">
                    The Study & Sermon Notes tool lets you build slide-based presentations for sermons, Bible studies, or personal devotions. Present live to an audience who can follow along in real time on their own devices — no app download required.
                  </p>
                </div>
                <ScreenshotPlaceholder alt="Study Notes editor" caption="The Study Notes editor showing slide list, verse input, and presenter notes panel" />

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Creating a Note</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>Navigate to <strong className="text-foreground">Tools → Study Notes</strong> from the top menu</li>
                    <li>Click <strong className="text-foreground">"New Note"</strong> to open the editor</li>
                    <li>Enter a title and optional description for your note</li>
                    <li>Add slides using the <strong className="text-foreground">"Add Slide"</strong> button — each slide can contain a verse reference, custom text, or presenter notes</li>
                    <li>For verse slides, type the reference (e.g., <em>John 3:16</em>) and select your Bible version — the verse text is validated in real time</li>
                    <li>Add <strong className="text-foreground">Presenter Notes</strong> per slide to keep your talking points handy during the presentation</li>
                    <li>Click <strong className="text-foreground">Save</strong> when finished</li>
                  </ol>
                </div>

                <InfoBox variant="primary" icon={Monitor} title="Presenter Mode">
                  <p>
                    Click <strong>Present</strong> on any saved note to enter Presenter Mode. You'll see a full-screen view with your slides, a slide jump-to selector, presenter notes panel, and live Q&amp;A controls. Use the <strong>fullscreen button</strong> (or press <kbd className="px-1.5 py-0.5 bg-primary/20 border border-primary/30 rounded text-xs font-mono">F</kbd>) for distraction-free presenting.
                  </p>
                </InfoBox>
                <ScreenshotPlaceholder alt="Presenter Mode full screen" caption="Presenter Mode showing the current slide, presenter notes, and audience join code" />

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Live Audience Sync</h3>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    When you start a presentation, a unique <strong className="text-foreground">4-digit join code</strong> and <strong className="text-foreground">QR code</strong> are generated. Share either with your audience so they can follow along on their phones at <strong className="text-foreground">fortifiedmind.org/live/[slug]</strong>.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Audience slides advance automatically as you move through the presentation</li>
                    <li>Verse text is displayed in the audience's chosen Bible version</li>
                    <li>Audience members can react with 👍 🙏 ✝️ emoji in real time</li>
                    <li>Audience members can submit questions via the Q&amp;A panel</li>
                  </ul>
                </div>

                <InfoBox variant="success" icon={QrCode} title="Sharing the Presentation">
                  <p>
                    Audience members can also share the live link with others directly from their device. The audience view includes a <strong>Share</strong> button that opens a QR code or a native mobile share sheet, making it easy to invite latecomers mid-service.
                  </p>
                </InfoBox>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Q&amp;A Panel</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Toggle the Q&amp;A feature on or off from the presenter toolbar. When enabled, audience members can submit questions that appear in your presenter panel. You can delete questions or regenerate the join code at any time during the presentation.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Presenter Keyboard Shortcuts</h3>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <ul className="space-y-2 text-muted-foreground">
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">→ / Space</kbd> — Next slide</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">←</kbd> — Previous slide</li>
                      <li><kbd className="px-2 py-1 bg-background border border-border rounded text-xs font-mono">F</kbd> — Toggle fullscreen</li>
                    </ul>
                  </div>
                </div>

                <InfoBox variant="warning" icon={Users} title="No Account Required for Audience">
                  <p>
                    Audience members do <strong>not</strong> need a Fortified Mind account to join a live presentation. They simply scan the QR code or visit the link and enter the 4-digit join code to follow along.
                  </p>
                </InfoBox>
              </div>
            </section>

            {/* ── STUDY ANALYTICS ── */}
            <section id="analytics" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={BarChart3} title="Study Analytics" />
              <div className="space-y-6">
                <ScreenshotPlaceholder alt="Study Analytics dashboard" caption="The Study Analytics dashboard showing streak, phase distribution chart, and review history calendar" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">What's Tracked</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Daily Streak:</strong> Consecutive days you've completed at least one review</li>
                    <li><strong className="text-foreground">Verses by Phase:</strong> Distribution of your verses across all memorization phases</li>
                    <li><strong className="text-foreground">Review History:</strong> Calendar view of past review activity with daily review counts</li>
                    <li><strong className="text-foreground">Mastery Progress:</strong> How many verses have reached Phase 5+ (fully mastered)</li>
                    <li><strong className="text-foreground">Verse Engagement:</strong> Community-wide engagement stats for verses you've studied</li>
                    <li><strong className="text-foreground">Total Reviews Completed:</strong> Cumulative count of all review sessions</li>
                    <li><strong className="text-foreground">Average Accuracy:</strong> Your overall recall success rate across all reviews</li>
                    <li><strong className="text-foreground">Reading Progress:</strong> Chapters and books read through the Bible Reader and reading plans</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Maintaining Your Streak</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Complete at least one review each day to maintain your streak. The dashboard shows your current streak prominently. Set up daily reminders in <strong className="text-foreground">Profile → Notifications</strong> to stay consistent. Your longest streak is also recorded so you can track your personal best.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Analytics Preview in Verse Library</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    A condensed analytics preview card is also available at the top of the Verse Library page, showing your key stats without leaving your verse management workflow. Click <strong className="text-foreground">"View Full Analytics"</strong> to open the complete Study Analytics dashboard.
                  </p>
                </div>
              </div>
            </section>

            {/* ── TALENTS & AWARDS ── */}
            <section id="awards" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Trophy} title="Talents & Awards" />
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">How Awards Work</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Fortified Mind awards <strong className="text-foreground">Talents</strong> (our version of badges) as you reach scripture memory milestones. Talents are earned automatically when you hit specific goals — no manual claiming required.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Award Categories</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Verse Milestones:</strong> Awarded for memorizing 1, 5, 10, 25, 50, 100+ verses</li>
                    <li><strong className="text-foreground">Streak Awards:</strong> Awarded for maintaining 7, 30, 90, 365-day review streaks</li>
                    <li><strong className="text-foreground">Reading Plan Awards:</strong> Awarded when you complete reading plans</li>
                    <li><strong className="text-foreground">Mastery Awards:</strong> Awarded when verses reach Phase 5+ (fully mastered)</li>
                    <li><strong className="text-foreground">Community Awards:</strong> Awarded for sharing reflections and engaging with others</li>
                    <li><strong className="text-foreground">Scripture Scholar:</strong> Awarded for reaching advanced memorization milestones</li>
                    <li><strong className="text-foreground">Perfect Practice:</strong> Awarded for completing review sessions with 100% accuracy</li>
                  </ul>
                </div>
                <InfoBox variant="accent" icon={Star} title="Talent Notifications">
                  <p>When you earn a new Talent, a toast notification appears on screen. You can view all your earned Talents in <strong>Profile → Talents & Awards</strong>.</p>
                </InfoBox>
              </div>
            </section>

            {/* ── NOTIFICATIONS ── */}
            <section id="notifications" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Bell} title="Notifications & Reminders" />
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Setting Up Daily Reminders</h3>
                  <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
                    <li>Go to <strong className="text-foreground">Profile</strong> (top right menu)</li>
                    <li>Click the <strong className="text-foreground">Notifications</strong> tab</li>
                    <li>Enable <strong className="text-foreground">Daily Study Reminders</strong></li>
                    <li>Set your preferred reminder time and frequency</li>
                    <li>Opt in to Reading Plan emails if desired</li>
                    <li>Click <strong className="text-foreground">Save Profile</strong> to apply changes</li>
                  </ol>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Available Notification Types</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Daily Study Reminders:</strong> A daily nudge to complete your review</li>
                    <li><strong className="text-foreground">Reading Plan Emails:</strong> Email updates about your reading plan progress</li>
                    <li><strong className="text-foreground">Bible Reading Reminders:</strong> Reminders specific to your Bible reading schedule</li>
                    <li><strong className="text-foreground">EGW Reading Reminders:</strong> Reminders for your Ellen G. White reading plan</li>
                    <li><strong className="text-foreground">Study Plan Updates:</strong> Notifications about milestones and plan completions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Content Preferences</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Customize what appears in your reminders: include a daily Bible verse, an EGW quote, or both. Choose your preferred Bible version and EGW content source in the Notifications settings.
                  </p>
                </div>
              </div>
            </section>

            {/* ── PROFILE & SETTINGS ── */}
            <section id="profile" className="bg-card border border-border rounded-lg p-6 shadow-warm scroll-mt-24">
              <SectionHeader icon={Settings} title="Profile & Settings" />
              <div className="space-y-6">
                <ScreenshotPlaceholder alt="User Profile page" caption="The User Profile page showing tabs for Account, Privacy, Notifications, Advanced, and Talents" />
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Profile Tabs Overview</h3>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong className="text-foreground">Profile:</strong> Update your avatar, username, and display name</li>
                    <li><strong className="text-foreground">Account:</strong> Manage your email, password, and timezone</li>
                    <li><strong className="text-foreground">Privacy:</strong> Control reflection visibility and community interactions</li>
                    <li><strong className="text-foreground">Notifications:</strong> Configure reminders, email preferences, and content preferences</li>
                    <li><strong className="text-foreground">Advanced:</strong> Customize memorization phase durations, daily review limits, and data export/import</li>
                    <li><strong className="text-foreground">Talents & Awards:</strong> View all earned badges and milestones</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Avatar Builder</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Personalize your profile with a custom avatar. Choose from various styles, colors, and accessories. Your avatar appears on your profile and next to your public reflections in the community.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Advanced Memorization Settings</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Fine-tune the spaced repetition system to match your learning pace. Adjust Phase 1 duration, daily/weekly/monthly review periods, daily review limits, and how frequently new verses are added from your future verses bucket. Access these in <strong className="text-foreground">Profile → Advanced</strong>.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Privacy Controls</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Control who can see your reflections and profile activity. Set your default reflection visibility (public or private), opt out of community engagement features, and manage your data in <strong className="text-foreground">Profile → Privacy</strong>.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">Username Manager</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Choose a unique username that appears on your public profile and next to your reflections. Usernames must be unique across the platform. Update your username anytime from <strong className="text-foreground">Profile → Account</strong>.
                  </p>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-foreground mb-3">Ready to Get Started?</h3>
              <p className="text-muted-foreground mb-6">
                Begin your scripture memory journey today. Start with the Bible Reader to find verses that speak to your heart.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/bible-reader"
                  className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 shadow-warm"
                >
                  <BookOpen className="w-5 h-5" />
                  <span>Open Bible Reader</span>
                </Link>
                <Link
                  to="/contact-us"
                  className="inline-flex items-center space-x-2 bg-card border border-border text-foreground px-6 py-3 rounded-lg font-semibold hover:bg-muted transition-colors duration-200"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>Contact Us</span>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}